import { createServerFn } from "@tanstack/react-start";
import * as Sentry from "@sentry/tanstackstart-react";
import { desc, eq, sql } from "drizzle-orm";

import { db } from "#/db";
import { bookEpisodes, books, episodeLinks, episodes } from "#/db/schema";
import {
  buildEpisodeViews,
  emptyGraphData,
  fallbackBookDetail,
  fallbackBooks,
  fallbackEpisodes,
} from "./bookling-data";

import type {
  BookDetailView,
  BookView,
  EpisodeView,
  GraphBookReference,
  GraphData,
  GuestGraphEdge,
  GuestGraphNode,
} from "./bookling-data";

const guestId = (guest: string) =>
  guest
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "unknown";

const edgeId = (a: string, b: string) => [a, b].sort().join("~~");

function getSet<K, V>(map: Map<K, Set<V>>, key: K): Set<V> {
  let set = map.get(key);
  if (!set) {
    set = new Set<V>();
    map.set(key, set);
  }
  return set;
}

function addRelationshipType(
  edge: GuestGraphEdge,
  type: GuestGraphEdge["relationshipTypes"][number],
) {
  if (!edge.relationshipTypes.includes(type)) {
    edge.relationshipTypes.push(type);
  }
}

function emptyEdge({
  id,
  source,
  target,
  sourceGuest,
  targetGuest,
}: Pick<
  GuestGraphEdge,
  "id" | "source" | "target" | "sourceGuest" | "targetGuest"
>): GuestGraphEdge {
  return {
    id,
    source,
    target,
    sourceGuest,
    targetGuest,
    sharedBooks: [],
    references: [],
    sharedBookCount: 0,
    referenceCount: 0,
    signalWeight: 0,
    relationshipTypes: [],
  };
}

export const listBooks = createServerFn({ method: "GET" }).handler(
  async (): Promise<Array<BookView>> => {
    return Sentry.startSpan(
      { name: "List books with mention counts", op: "db.d1.select" },
      async () => {
        try {
          const mentionCount = sql<number>`count(${bookEpisodes.bookId})`;
          const rows = await db
            .select({
              id: books.id,
              title: books.title,
              author: books.author,
              slug: books.slug,
              imageUrl: books.imageUrl,
              mentionCount,
            })
            .from(books)
            .leftJoin(bookEpisodes, eq(bookEpisodes.bookId, books.id))
            .groupBy(books.id)
            .orderBy(desc(mentionCount), books.title);

          if (rows.length === 0) {
            return fallbackBooks;
          }

          return rows.map((row) => ({
            ...row,
            mentionCount: Number(row.mentionCount ?? 0),
          }));
        } catch (error) {
          Sentry.captureException(error);
          return fallbackBooks;
        }
      },
    );
  },
);

export const getBookBySlug = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  .handler(async ({ data: slug }): Promise<BookDetailView | null> => {
    return Sentry.startSpan({ name: "Get book by slug", op: "db.d1.select" }, async () => {
      try {
        const [bookRow] = await db
          .select({
            id: books.id,
            title: books.title,
            author: books.author,
            slug: books.slug,
            imageUrl: books.imageUrl,
            amazonUrl: books.amazonUrl,
            rating: books.rating,
          })
          .from(books)
          .where(eq(books.slug, slug))
          .limit(1);

        if (!bookRow) {
          if (slug === fallbackBookDetail.slug) return fallbackBookDetail;
          return null;
        }

        const episodeRows = await db
          .select({
            id: episodes.id,
            number: episodes.number,
            title: episodes.title,
            guest: episodes.guest,
            company: episodes.company,
            guestImage: episodes.guestImage,
            applePodcastLink: episodes.applePodcastLink,
            youtubeLink: episodes.youtubeLink,
            spotifyLink: episodes.spotifyLink,
            context: bookEpisodes.context,
            timestamp: bookEpisodes.timestamp,
          })
          .from(bookEpisodes)
          .innerJoin(episodes, eq(bookEpisodes.episodeId, episodes.id))
          .where(eq(bookEpisodes.bookId, bookRow.id))
          .orderBy(desc(episodes.number));

        return { ...bookRow, episodes: episodeRows };
      } catch (error) {
        Sentry.captureException(error);
        if (slug === fallbackBookDetail.slug) return fallbackBookDetail;
        return null;
      }
    });
  });

export const listEpisodes = createServerFn({ method: "GET" }).handler(
  async (): Promise<Array<EpisodeView>> => {
    return Sentry.startSpan({ name: "List episodes with books", op: "db.d1.select" }, async () => {
      try {
        const rows = await db
          .select({
            id: episodes.id,
            number: episodes.number,
            title: episodes.title,
            guest: episodes.guest,
            company: episodes.company,
            guestImage: episodes.guestImage,
            applePodcastLink: episodes.applePodcastLink,
            youtubeLink: episodes.youtubeLink,
            spotifyLink: episodes.spotifyLink,
            bookId: books.id,
            bookTitle: books.title,
            bookAuthor: books.author,
            bookSlug: books.slug,
            bookImage: books.imageUrl,
            context: bookEpisodes.context,
            timestamp: bookEpisodes.timestamp,
          })
          .from(episodes)
          .leftJoin(bookEpisodes, eq(bookEpisodes.episodeId, episodes.id))
          .leftJoin(books, eq(bookEpisodes.bookId, books.id))
          .orderBy(desc(episodes.number));

        if (rows.length === 0) {
          return fallbackEpisodes;
        }

        return buildEpisodeViews(rows);
      } catch (error) {
        Sentry.captureException(error);
        return fallbackEpisodes;
      }
    });
  },
);

export const getGraphData = createServerFn({ method: "GET" }).handler(
  async (): Promise<GraphData> => {
    return Sentry.startSpan({ name: "Build GDIY guest graph", op: "db.d1.graph" }, async () => {
      try {
        const episodeRows = await db
          .select({
            id: episodes.id,
            number: episodes.number,
            title: episodes.title,
            guest: episodes.guest,
            company: episodes.company,
            guestImage: episodes.guestImage,
          })
          .from(episodes);

        if (episodeRows.length === 0) return emptyGraphData;

        const episodeByNumber = new Map<number, (typeof episodeRows)[number]>();
        const nodeMap = new Map<string, GuestGraphNode>();
        const guestBooks = new Map<string, Set<number>>();

        for (const episode of episodeRows) {
          episodeByNumber.set(episode.number, episode);
          const id = guestId(episode.guest);
          const existing = nodeMap.get(id);
          if (existing) {
            existing.episodeNumbers.push(episode.number);
            existing.episodeTitles.push(episode.title);
            existing.company ||= episode.company;
            existing.guestImage ||= episode.guestImage;
          } else {
            nodeMap.set(id, {
              id,
              guest: episode.guest,
              company: episode.company,
              guestImage: episode.guestImage,
              episodeNumbers: [episode.number],
              episodeTitles: [episode.title],
              bookCount: 0,
              connectionCount: 0,
              referenceInCount: 0,
              referenceOutCount: 0,
              centralityScore: 0,
            });
          }
        }

        const mentionRows = await db
          .select({
            bookId: books.id,
            bookTitle: books.title,
            bookAuthor: books.author,
            bookSlug: books.slug,
            episodeNumber: episodes.number,
            episodeTitle: episodes.title,
            guest: episodes.guest,
          })
          .from(bookEpisodes)
          .innerJoin(books, eq(bookEpisodes.bookId, books.id))
          .innerJoin(episodes, eq(bookEpisodes.episodeId, episodes.id));

        const bookGuests = new Map<
          number,
          {
            book: GraphBookReference;
            guests: Map<string, { guest: string; episodes: Set<number> }>;
          }
        >();

        for (const row of mentionRows) {
          const gId = guestId(row.guest);
          getSet(guestBooks, gId).add(row.bookId);

          let bookEntry = bookGuests.get(row.bookId);
          if (!bookEntry) {
            bookEntry = {
              book: {
                id: row.bookId,
                title: row.bookTitle,
                author: row.bookAuthor,
                slug: row.bookSlug,
              },
              guests: new Map(),
            };
            bookGuests.set(row.bookId, bookEntry);
          }
          const guestEntry = bookEntry.guests.get(gId) ?? {
            guest: row.guest,
            episodes: new Set<number>(),
          };
          guestEntry.episodes.add(row.episodeNumber);
          bookEntry.guests.set(gId, guestEntry);
        }

        for (const [gId, bookSet] of guestBooks.entries()) {
          const node = nodeMap.get(gId);
          if (node) node.bookCount = bookSet.size;
        }

        const edgeMap = new Map<string, GuestGraphEdge>();
        const bookConnectorScores = new Map<
          number,
          GraphBookReference & { connectorScore: number }
        >();

        for (const bookEntry of bookGuests.values()) {
          const guests = Array.from(bookEntry.guests.entries());
          if (guests.length < 2) continue;

          const connectorScore = (guests.length * (guests.length - 1)) / 2;
          bookConnectorScores.set(bookEntry.book.id, {
            ...bookEntry.book,
            guestCount: guests.length,
            connectorScore,
          });

          const weightedSignal = 1 / Math.log2(2 + guests.length);
          for (let i = 0; i < guests.length; i += 1) {
            for (let j = i + 1; j < guests.length; j += 1) {
              const [sourceId, source] = guests[i];
              const [targetId, target] = guests[j];
              if (sourceId === targetId) continue;
              const id = edgeId(sourceId, targetId);
              const existing =
                edgeMap.get(id) ??
                emptyEdge({
                  id,
                  source: sourceId,
                  target: targetId,
                  sourceGuest: source.guest,
                  targetGuest: target.guest,
                });
              existing.sharedBooks.push({
                ...bookEntry.book,
                guestCount: guests.length,
              });
              existing.sharedBookCount = existing.sharedBooks.length;
              existing.signalWeight += weightedSignal;
              addRelationshipType(existing, "shared_books");
              edgeMap.set(id, existing);
            }
          }
        }

        const referenceRows = await db
          .select({
            sourceEpisodeNumber: episodeLinks.sourceEpisodeNumber,
            targetEpisodeNumber: episodeLinks.targetEpisodeNumber,
            sourceGuest: episodeLinks.sourceGuest,
            targetGuest: episodeLinks.targetGuest,
            context: episodeLinks.context,
          })
          .from(episodeLinks);

        const referencePairs = new Set<string>();
        for (const row of referenceRows) {
          const sourceEpisode = episodeByNumber.get(row.sourceEpisodeNumber);
          const targetEpisode = episodeByNumber.get(row.targetEpisodeNumber);
          if (!sourceEpisode || !targetEpisode) continue;

          const sourceGuest = row.sourceGuest || sourceEpisode.guest;
          const targetGuest = row.targetGuest || targetEpisode.guest;
          const sourceId = guestId(sourceGuest);
          const targetId = guestId(targetGuest);
          if (sourceId === targetId) continue;

          referencePairs.add(`${sourceId}->${targetId}`);
          const sourceNode = nodeMap.get(sourceId);
          const targetNode = nodeMap.get(targetId);
          if (sourceNode) sourceNode.referenceOutCount += 1;
          if (targetNode) targetNode.referenceInCount += 1;

          const id = edgeId(sourceId, targetId);
          const existing =
            edgeMap.get(id) ??
            emptyEdge({
              id,
              source: sourceId,
              target: targetId,
              sourceGuest,
              targetGuest,
            });
          existing.references.push({
            sourceEpisodeNumber: row.sourceEpisodeNumber,
            targetEpisodeNumber: row.targetEpisodeNumber,
            sourceEpisodeTitle: sourceEpisode.title,
            targetEpisodeTitle: targetEpisode.title,
            sourceGuest,
            targetGuest,
            context: row.context,
          });
          existing.referenceCount = existing.references.length;
          existing.signalWeight += 1.25;
          addRelationshipType(existing, "episode_reference");
          edgeMap.set(id, existing);
        }

        const connectionSets = new Map<string, Set<string>>();
        for (const edge of edgeMap.values()) {
          getSet(connectionSets, edge.source).add(edge.target);
          getSet(connectionSets, edge.target).add(edge.source);
        }

        for (const [id, node] of nodeMap.entries()) {
          node.connectionCount = connectionSets.get(id)?.size ?? 0;
          node.episodeNumbers.sort((a, b) => b - a);
          node.centralityScore =
            node.connectionCount * 2 +
            node.referenceInCount * 3 +
            node.referenceOutCount * 1.5 +
            node.bookCount;
        }

        const edges = Array.from(edgeMap.values()).sort((a, b) => b.signalWeight - a.signalWeight);
        const nodes = Array.from(nodeMap.values()).sort(
          (a, b) => b.centralityScore - a.centralityScore,
        );

        const sharedBookEdgeCount = edges.filter((edge) => edge.sharedBookCount > 0).length;
        const episodeReferenceEdgeCount = referencePairs.size;
        const strongestBookPairs = edges.filter((edge) => edge.sharedBookCount > 0).slice(0, 8);
        const mostReferencedGuests = [...nodes]
          .filter((node) => node.referenceInCount > 0)
          .sort((a, b) => b.referenceInCount - a.referenceInCount)
          .slice(0, 8);
        const mostSharedBooks = Array.from(bookConnectorScores.values())
          .sort((a, b) => b.connectorScore - a.connectorScore)
          .slice(0, 8);
        const bridgeGuests = [...nodes].filter((node) => node.connectionCount > 0).slice(0, 8);

        return {
          nodes,
          edges,
          stats: {
            guestCount: nodes.length,
            episodeCount: episodeRows.length,
            bookCount: bookGuests.size,
            sharedBookEdgeCount,
            episodeReferenceEdgeCount,
            strongestBookPairs,
            mostReferencedGuests,
            mostSharedBooks,
            bridgeGuests,
          },
        };
      } catch (error) {
        Sentry.captureException(error);
        return emptyGraphData;
      }
    });
  },
);
